import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Card, Form, Modal, Table } from 'react-bootstrap';
import CurrencyMasterApi from '../../../api/CurrencyMasterApi';

const defaultForm = {
  currency_code: '',
  currency_name: '',
  symbol: '',
  is_active: true,
};

const CurrencyMasterManager = () => {
  const api = useMemo(() => new CurrencyMasterApi(), []);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [saving, setSaving] = useState(false);

  const loadList = useCallback(async () => {
    setLoading(true);
    setMessage('');
    try {
      const res = await api.fetchAll();
      const data = res?.data;
      if (data?.success) {
        setList(data.data || []);
      } else {
        setMessage(data?.message || 'Failed to load currencies');
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || err?.message || 'Failed to load currencies');
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const openCreate = () => {
    setEditingCode(null);
    setForm(defaultForm);
    setShowModal(true);
  };

  const openEdit = (row) => {
    setEditingCode(row.currency_code);
    setForm({
      currency_code: row.currency_code || '',
      currency_name: row.currency_name || '',
      symbol: row.symbol ?? '',
      is_active: Boolean(row.is_active),
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCode(null);
    setForm(defaultForm);
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.currency_code?.trim() || !form.currency_name?.trim()) {
      setMessage('Currency code and name are required.');
      return;
    }
    setSaving(true);
    setMessage('');
    try {
      if (editingCode) {
        const res = await api.updateByCode(
          { currency_name: form.currency_name, symbol: form.symbol, is_active: form.is_active },
          editingCode
        );
        if (res?.data?.success) {
          closeModal();
          loadList();
        } else {
          setMessage(res?.data?.message || 'Update failed');
        }
      } else {
        const res = await api.create(form);
        if (res?.data?.success) {
          closeModal();
          loadList();
        } else {
          setMessage(res?.data?.message || 'Create failed');
        }
      }
    } catch (err) {
      setMessage(err?.response?.data?.message || err?.message || 'Request failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wrapper">
      <div id="content">
        <Card className="p-4">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="mb-0">Manage Currency</h4>
            <Button variant="primary" onClick={openCreate}>
              Add Currency
            </Button>
          </div>
          {message && (
            <div className="alert alert-info mb-3" role="alert">
              {message}
            </div>
          )}
          {loading ? (
            <p className="text-muted">Loading...</p>
          ) : (
            <Table responsive bordered hover>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Name</th>
                  <th>Symbol</th>
                  <th>Active</th>
                  <th className="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                {list.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-muted text-center py-4">
                      No currencies. Click &quot;Add Currency&quot; to add one.
                    </td>
                  </tr>
                ) : (
                  list.map((row) => (
                    <tr key={row.currency_code}>
                      <td>{row.currency_code}</td>
                      <td>{row.currency_name}</td>
                      <td>{row.symbol ?? '—'}</td>
                      <td>{row.is_active ? 'Yes' : 'No'}</td>
                      <td className="text-end">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => openEdit(row)}
                        >
                          Edit
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          )}
        </Card>

        <Modal show={showModal} onHide={closeModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>{editingCode ? 'Edit Currency' : 'Add Currency'}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Currency Code *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. USD, EUR"
                  value={form.currency_code}
                  onChange={(e) => handleChange('currency_code', e.target.value)}
                  maxLength={10}
                  disabled={!!editingCode}
                />
                {editingCode && (
                  <Form.Text className="text-muted">Code cannot be changed after create.</Form.Text>
                )}
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Currency Name *</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. US Dollar"
                  value={form.currency_name}
                  onChange={(e) => handleChange('currency_name', e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Symbol</Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g. $, €"
                  value={form.symbol}
                  onChange={(e) => handleChange('symbol', e.target.value)}
                  maxLength={20}
                />
              </Form.Group>
              <Form.Check
                type="switch"
                id="master_is_active"
                label="Active"
                checked={form.is_active}
                onChange={(e) => handleChange('is_active', e.target.checked)}
              />
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={closeModal} type="button">
                Cancel
              </Button>
              <Button variant="primary" type="submit" disabled={saving}>
                {saving ? 'Saving...' : editingCode ? 'Update' : 'Create'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </div>
    </div>
  );
};

export default CurrencyMasterManager;
